import { matchRoutes } from 'react-router-dom';
import { 
  initializeFaro, 
  createReactRouterV6DataOptions, 
  ReactIntegration, 
  getWebInstrumentations,
  FetchTransport,
  ConsoleTransport,
  type Faro,
  LogLevel,
  BaseTransport
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import type { BrowserRouter } from 'react-router-dom';

interface View {
  name: string;
  attributes?: Record<string, unknown>;
}

// Extend Window interface to include Faro
declare global {
  interface Window {
    faro?: {
      api?: {
        pushEvent: (name: string, attributes?: Record<string, unknown>) => void;
        pushError: (error: Error, context?: Record<string, unknown>) => void;
        pushView: (view: View) => void;
      };
    };
  }
}

// Override console to filter out Faro logs
const originalConsoleLog = console.log.bind(console);
console.log = (...args: any[]) => {
  if (!(typeof args[0] === 'string' && args[0].includes('Faro'))) {
    originalConsoleLog(...args);
  }
};

export const initializeMonitoring = () => {
  const collectorUrl = process.env.NODE_ENV === 'development'
    ? '/collect'
    : import.meta.env.VITE_GRAFANA_COLLECTOR_URL;

  if (!collectorUrl) {
    console.warn('Grafana collector URL is not configured. Monitoring will be disabled.');
    return;
  }
  
  const config = {
    app: {
      name: 'Wechter App',
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      environment: process.env.NODE_ENV
    },
    sessionTracking: {
      samplingRate: 1,
      persistent: true
    },
    instrumentations: [
      // Load the default Web instrumentations
      ...getWebInstrumentations(),
      // Add tracing instrumentation
      new TracingInstrumentation(),
      // Add React integration with router instrumentation
      new ReactIntegration({
        router: createReactRouterV6DataOptions({
          matchRoutes,
        }),
      }),
    ],
    transports: [
      // Always include the Fetch transport for sending data to Grafana
      new FetchTransport({
        url: collectorUrl,
        requestOptions: {
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json'
          }
        }
        })
    ] as BaseTransport[]
  };

  initializeFaro(config);
};

// Helper function to log errors with original messages
export const logError = (error: Error, translationKey?: string, context?: Record<string, unknown>) => {
  if (window.faro?.api) {
    // Create a structured error object
    const errorContext = {
      ...context,
      originalMessage: error.message,
      ...(translationKey && { translationKey }),
      timestamp: new Date().toISOString(),
      errorName: error.name,
      ...(error.stack && { stack: error.stack.split('\n').slice(0, 3).join('\n') })
    };

    // Log to Faro
    window.faro.api.pushError(error, errorContext);

    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[Faro Error]', {
        error,
        context: errorContext
      });
    }
  }
};

// Helper function to get a clean route name
const getRouteName = (pathname: string): string => {
  // Remove trailing slash
  const cleanPath = pathname.replace(/\/$/, '');
  
  // If it's the root path, return 'home'
  if (cleanPath === '') return 'home';
  
  // Remove leading slash and replace remaining slashes with dots
  return cleanPath.replace(/^\//, '').replace(/\//g, '.');
};

export const withFaroRouterInstrumentation = (router: any) => {
  // Add a subscriber to track navigation events
  router.subscribe((state: any) => {
    // Get the current location
    const location = state.location;
    
    // Get the matched routes
    const matches = matchRoutes(router.routes, location);
    
    if (matches) {
      // Get the deepest matched route (current page)
      const currentRoute = matches[matches.length - 1];
      
      // Get a clean route name for the view
      const routeName = getRouteName(location.pathname);
      
      // Push both a view and an event for better tracking
      if (window.faro?.api?.pushView && window.faro?.api?.pushEvent) {
        try {
          // Push view for page performance tracking
          window.faro.api.pushView({
            name: routeName,
            attributes: {
              path: location.pathname,
              route: currentRoute.route.path || '/',
              search: location.search || '',
              hash: location.hash || '',
              timestamp: new Date().toISOString()
            }
          });

          // Push event for custom tracking
          window.faro.api.pushEvent('page_view', {
            view: routeName,
            path: location.pathname,
            route: currentRoute.route.path || '/',
            search: location.search || '',
            hash: location.hash || '',
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.warn('Failed to push Faro tracking event:', error);
        }
      }
    }
  });

  return router;
}; 
